import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Escarabajo de tinta (models/beetle.ts), intact or cracked (both share the skeleton). Idle and walk loop (a true
 * alternating tripod: right-front, left-middle and right-rear swing while the other three push), attack comes in
 * already cocked and spits acid with a snap of the mandibles at ~0.13 s, hit, stun (loop), die (it rolls onto its
 * back and the legs curl up over the belly; the view then sinks it) and `crack`, the moment a heavy blow splits
 * the shell: the plates jump on their hinges, it rears and shudders.
 *
 * Conventions: +X on the body or head pitches the nose down (and the tail up); +Y on `mandR` (and −Y on `mandL`)
 * opens the mandibles, likewise the antennae spread; +X on a wing-case lifts its rear, +Z on `shellR` (−Z on
 * `shellL`) swings its flank up and out. Each leg's local +Z runs out along it: −X on `leg<i>` lifts the leg, a Y
 * turn swings it (see `fwd`), +X on `knee<i>` tucks the foot in. Legs 0–2 are the right side, front to back, 3–5
 * the left.
 */

const LEGS = [0, 1, 2, 3, 4, 5] as const;
type LegB = `leg${(typeof LEGS)[number]}` | `knee${(typeof LEGS)[number]}`;
type B = 'root' | 'body' | 'abdomen' | 'shellL' | 'shellR' | 'head' | 'mandL' | 'mandR' | 'antL' | 'antR' | 'spit' | LegB;
type P = KeyPose<B>;

const legN = (i: number): B => `leg${i}` as B;
const kneeN = (i: number): B => `knee${i}` as B;
/** +1 right side, −1 left. */
const side = (i: number): number => (i < 3 ? 1 : -1);
/** 0 front, 1 middle, 2 rear. */
const row = (i: number): number => i % 3;
/** The alternating tripods: right-front, left-middle, right-rear (0) against the other three (1). */
const tripod = (i: number): number => (i === 0 || i === 4 || i === 2 ? 0 : 1);

/**
 * One leg: `lift` raises it off the ground (the knee folds to keep the foot tucked), `stride` swings the foot
 * forward (+) or back (−), `splay` drops the knee and pushes the foot out, `curl` folds the whole leg in.
 */
const leg = (b: Bones<B>, i: number, lift: number, stride: number, splay = 0, curl = 0): void => {
  // Turning about the hip's vertical: forward is −Y on the right side, +Y on the left. Front legs reach a little
  // farther, rear legs push a little farther.
  const fwd = -side(i) * stride * (row(i) === 1 ? 0.3 : 0.26);
  b[legN(i)].rot(-0.42 * lift + 0.3 * splay + 0.6 * curl, fwd, 0);
  b[kneeN(i)].rot(0.35 * lift - 0.55 * splay + 1.3 * curl, 0, 0);
};

/** A key pose of all six legs from a function of the leg index, plus anything else. */
const legsPose = (fn: (i: number) => readonly [number, number, number?, number?], rest: P = {}): P => {
  const p: Record<string, [number, number, number]> = {};
  for (const i of LEGS) {
    const [lift, stride, splay = 0, curl = 0] = fn(i);
    p[legN(i)] = [-0.42 * lift + 0.3 * splay + 0.6 * curl, -side(i) * stride * (row(i) === 1 ? 0.3 : 0.26), 0];
    p[kneeN(i)] = [0.35 * lift - 0.55 * splay + 1.3 * curl, 0, 0];
  }
  return { ...(p as P), ...rest };
};

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Whole-figure squash from impulses: a landing flattens it a hair and it springs back. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** A deterministic shiver (two sines at integer multiples of `f`). */
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 2 * t + 1.3);

/** Mandibles open by `o` (radians each). */
const jaws = (b: Bones<B>, o: number): void => {
  b.mandR.rot(0, o, 0);
  b.mandL.rot(0, -o, 0);
};

/** Antennae: `spread` out to the sides, `lift` up (− lowers them). */
const feelers = (b: Bones<B>, spread: number, lift: number, wl = 0, wr = 0): void => {
  b.antR.rot(-lift + wr, spread, 0);
  b.antL.rot(-lift + wl, -spread, 0);
};

/** Squat and wide, head a little low, mandibles just parted. */
const ST: P = legsPose(() => [0, 0, 0.08], {
  root: [0, 0, 0, 0, -0.01, 0],
  body: [0.03, 0, 0],
  head: [0.05, 0, 0],
  mandR: [0, 0.08, 0],
  mandL: [0, -0.08, 0],
});

/**
 * At rest it is never still: the mandibles work over each other, the antennae taste the air one then the other, the
 * abdomen pumps under the shell, the head turns to look and one leg after another shifts its grip.
 */
const idle = (): Clip<B> =>
  clip<B>('idle', 3.6, true, (b, t) => {
    const ph = phase(t, 3.6);
    const p = ph * TAU;
    apply(b, ST);
    const br = Math.sin(p * 2);
    b.abdomen.scale(1 + 0.025 * br).rot(-0.02 * br);
    b.body.rot(0.012 * br, 0, 0.01 * Math.sin(p)).pos(0, 0.006 * br, 0);
    b.shellL.rot(0.015 * Math.max(0, br), 0, -0.01 * Math.max(0, br));
    b.shellR.rot(0.015 * Math.max(0, br), 0, 0.01 * Math.max(0, br));
    // The head turns to look, holding at each end.
    const look = Math.sin(p);
    b.head.rot(0.04 * Math.sin(p * 2 + 1), 0.22 * Math.sign(look) * Math.abs(look) ** 0.5, 0.04 * look);
    // Mandibles chewing: quick work in bursts.
    const chew = Math.max(0, Math.sin(p * 3)) * Math.sin(p * 12);
    jaws(b, 0.1 + 0.14 * chew);
    // Antennae: slow sweeps with a flick now and then.
    const fl = Math.max(0, Math.sin(p * 2 - 1)) ** 6;
    const fr = Math.max(0, Math.sin(p * 2 + 2)) ** 6;
    feelers(b, 0.12 * Math.sin(p + 0.5), 0.1 + 0.08 * Math.sin(p * 2), -0.35 * fl, -0.35 * fr);
    b.antL.rot(0, -0.15 * Math.sin(p * 3), 0);
    b.antR.rot(0, 0.15 * Math.sin(p * 3 + 1), 0);
    // One leg at a time lifts and re-plants, round the body.
    for (const i of LEGS) {
      const d = (((ph * 2 - i / 6) % 1) + 1) % 1;
      const k = d < 0.1 ? Math.sin((d / 0.1) * Math.PI) : 0;
      leg(b, i, 0.5 * k, 0.2 * k, 0);
    }
  }, (t) => {
    const br = Math.sin(phase(t, 3.6) * TAU * 2);
    return [1 + 0.006 * br, 1 - 0.008 * br, 1 + 0.006 * br];
  });

/**
 * The tripod gait: three legs push the body forward while the other three swing through the air, then they
 * trade. The body surges on each push and rolls a little toward the planted pair; the head nods and the antennae
 * sweep the way ahead.
 */
const walk = (dur = 1.1): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    apply(b, ST, 0.9);
    for (const i of LEGS) {
      // Each leg's own phase: half the cycle on the ground sweeping back, half in the air swinging forward.
      const u = (ph + (tripod(i) ? 0.5 : 0) + (row(i) - 1) * 0.04 + 1) % 1;
      const swing = u >= 0.5;
      const k = swing ? (u - 0.5) / 0.5 : u / 0.5;
      const stride = swing ? -Math.cos(Math.PI * k) : Math.cos(Math.PI * k);
      const lift = swing ? Math.sin(Math.PI * k) ** 0.8 : 0;
      leg(b, i, 0.9 * lift, 1.1 * stride, 0.05);
    }
    // Two pushes a cycle: a surge and a dip, rolling toward whichever tripod has two feet on its side.
    const c2 = Math.cos(2 * th);
    b.root.pos(0, -0.008 - 0.01 * c2, 0.012 * Math.sin(2 * th));
    b.body.rot(0.02 * Math.sin(2 * th - 0.6), 0.05 * Math.sin(th), 0.04 * Math.sin(th - 0.4));
    b.abdomen.rot(0.02 * Math.cos(2 * th - 1), -0.04 * Math.sin(th - 0.8), 0);
    b.shellL.rot(0.008 * Math.max(0, -c2));
    b.shellR.rot(0.008 * Math.max(0, -c2));
    b.head.rot(0.04 * Math.sin(2 * th - 1.1), -0.08 * Math.sin(th - 0.3), -0.03 * Math.sin(th - 0.5));
    jaws(b, 0.06 * Math.sin(2 * th));
    feelers(b, 0.1 + 0.08 * Math.sin(th), 0.15, 0.15 * Math.sin(th * 2 - 1.5), 0.15 * Math.sin(th * 2 + 1.6));
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, dur) * TAU * 2)) ** 3;
    return [1 + 0.01 * k, 1 - 0.015 * k, 1 + 0.01 * k];
  });

/**
 * The spit: it comes in already reared, head thrown back, mandibles wide and the acid swelling in its throat;
 * at 0.13 s it snaps the head down and forward, the mandibles clap shut and the glob is flung out; it rocks back on
 * its haunches with the recoil and settles, a new glob welling up.
 */
const COCK: P = legsPose((i) => (row(i) === 0 ? [0.25, 0.2, 0] : row(i) === 2 ? [0, -0.25, 0.2] : [0, -0.1, 0.1]), {
  root: [0, 0, 0, 0, 0.02, -0.04],
  body: [-0.22, 0, 0],
  abdomen: [0.12, 0, 0, 0, 0, 0, 1.06],
  head: [-0.35, 0, 0],
  mandR: [0, 0.55, 0],
  mandL: [0, -0.55, 0],
  antR: [0.5, 0.35, 0],
  antL: [0.5, -0.35, 0],
  shellL: [0.06, 0, -0.04],
  shellR: [0.06, 0, 0.04],
  spit: [0, 0, 0, 0, 0, 0, 1.7],
});
const SNAP: P = legsPose((i) => (row(i) === 0 ? [0, 0.5, 0.2] : row(i) === 2 ? [0, -0.3, 0.1] : [0, -0.05, 0.15]), {
  root: [0, 0, 0, 0, -0.03, 0.08],
  body: [0.18, 0, 0],
  abdomen: [-0.06, 0, 0, 0, 0, 0, 0.96],
  head: [0.3, 0, 0],
  mandR: [0, -0.12, 0],
  mandL: [0, 0.12, 0],
  antR: [-0.2, 0.55, 0],
  antL: [-0.2, -0.55, 0],
  shellL: [-0.02, 0, 0.02],
  shellR: [-0.02, 0, -0.02],
});

const attack = (): Clip<B> =>
  clip<B>('attack', 0.9, false, (b, t) => {
    timeline(b, t, [
      [0, { ...ST, ...COCK, body: [-0.17, 0, 0], head: [-0.26, 0, 0] }],
      [0.07, COCK, outQuad],
      [0.13, SNAP, inCubic],
      [0.28, { ...SNAP, body: [0.1, 0, 0], head: [0.15, 0, 0], root: [0, 0, 0, 0, -0.02, 0.05] }, outCubic],
      [0.9, ST, inOut],
    ], { head: 0.015, mandL: 0.01, mandR: 0.01, antL: 0.04, antR: 0.04, abdomen: 0.03 });
    // The glob: swells in the throat, is flung out at the snap (and gone), then a new one wells up.
    const out = span(t, 0.1, 0.2, outQuad);
    const gone = span(t, 0.16, 0.22);
    const back = span(t, 0.4, 0.9, inQuad);
    const home = 1 - span(t, 0.24, 0.3);
    b.spit.pos(0, 0.05 * out * home, 0.55 * out * home).scale(Math.max(0.001, (1 - gone) * (1 + 0.6 * out) + back * gone));
    // The jolt of the snap through the body.
    const k = kick(t, 0.13, 4, 10);
    b.body.rot(0.04 * k);
    b.head.rot(0.06 * k);
  }, (t) => thud(t, [[0.13, 0.04]]));

/** The blow lands: it jerks back on its legs, the plates rattle, the mandibles gape. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.4, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.6, 9) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, -0.012 * a, -0.04 * k);
    b.body.rot(-0.1 * k, 0.04 * k, 0.05 * k);
    b.head.rot(-0.2 * k, -0.1 * k);
    jaws(b, 0.3 * a);
    feelers(b, 0.2 * a, 0.3 * k);
    const r = kick(t, 0.03, 7, 12);
    b.shellL.rot(0.05 * a + 0.02 * r, 0, -0.03 * r);
    b.shellR.rot(0.05 * a - 0.02 * r, 0, 0.03 * r);
    for (const i of LEGS) leg(b, i, 0, 0, 0.25 * a);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  });

/** Dazed: the body sways round on legs that twitch out of time, the head hangs, the antennae droop. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, ST, 0.6);
    b.root.pos(0.02 * Math.cos(p), -0.03, 0).rot(0, 0, 0.05 * Math.cos(p));
    b.body.rot(0.08 + 0.04 * Math.sin(p * 2), 0.08 * Math.sin(p), 0.04 * Math.cos(p));
    b.head.rot(0.3 + 0.08 * Math.sin(p + 0.8), 0.25 * Math.cos(p + 0.8), 0.15 * Math.sin(p + 0.8));
    jaws(b, 0.35 + 0.08 * Math.sin(p * 3));
    feelers(b, 0.25, -0.45 + 0.1 * Math.sin(p * 2), 0.1 * Math.sin(p * 3), 0.1 * Math.sin(p * 3 + 2));
    b.abdomen.scale(1 + 0.02 * Math.sin(p * 2));
    for (const i of LEGS) {
      const tw = Math.max(0, Math.sin(p * 3 + i * 1.7)) ** 4;
      leg(b, i, 0.3 * tw, 0.3 * Math.sin(p * 2 + i * 2.1) * tw, 0.3 + 0.1 * Math.sin(p + i));
    }
  });

/**
 * Death: it rears with a shudder, the legs on its left give and it tips onto its flank, then rolls over onto its
 * back with a thud; there the legs thrash, slow, and curl up over the belly.
 */
const die = (): Clip<B> =>
  clip<B>('die', 2.4, false, (b, t) => {
    const onBack: P = legsPose((i) => [0, 0.2 * (1 - row(i)), 0, 0.35]);
    const curled: P = legsPose((i) => [0, 0.35 * (1 - row(i)), 0, 0.85 + 0.05 * row(i)]);
    timeline(b, t, [
      [0, ST],
      [0.22, { ...ST, body: [-0.25, 0, 0.05], head: [-0.35, 0.1, 0], mandR: [0, 0.6, 0], mandL: [0, -0.6, 0], antR: [0.5, 0.4, 0], antL: [0.5, -0.4, 0], shellL: [0.05, 0, -0.05], shellR: [0.05, 0, 0.05] }, outExpo],
      [0.6, { ...legsPose((i) => (side(i) < 0 ? [0, 0, 0.9] : [0.4, 0, -0.2])), root: [0, 0, 0.55, -0.08, 0.06, 0], body: [0.05, 0, 0], head: [0.2, 0.2, 0.2], mandR: [0, 0.3, 0], mandL: [0, -0.3, 0] }, inOut],
      [0.92, { ...legsPose(() => [0.5, 0, 0, 0.2]), root: [0, 0, 1.6, -0.3, 0.42, 0], head: [-0.2, 0.3, 0.3], mandR: [0, 0.5, 0], mandL: [0, -0.5, 0] }, inQuad],
      [1.18, { ...onBack, root: [0, 0, Math.PI, -0.12, 0.66, 0], head: [-0.45, 0.2, 0], mandR: [0, 0.6, 0], mandL: [0, -0.6, 0], antR: [-0.2, 0.4, 0], antL: [-0.2, -0.4, 0] }, inQuad],
      [1.32, { ...onBack, root: [0, 0, Math.PI - 0.08, -0.12, 0.68, 0], head: [-0.4, 0.2, 0], mandR: [0, 0.6, 0], mandL: [0, -0.6, 0], antR: [-0.2, 0.4, 0], antL: [-0.2, -0.4, 0] }, outBack],
      [2.4, { ...curled, root: [0, 0, Math.PI - 0.06, -0.12, 0.67, 0], head: [-0.3, 0.1, 0], mandR: [0, 0.25, 0], mandL: [0, -0.25, 0], antR: [0.2, 0.2, 0], antL: [0.2, -0.2, 0] }, inOut],
    ], { head: 0.04, antL: 0.06, antR: 0.06, abdomen: 0.05 });
    // On its back the legs thrash, slower and slower.
    const th = envelope(t, 1.2, 1.3, 1.6, 2.2, outQuad);
    for (const i of LEGS) {
      const w = Math.sin(TAU * 4 * t + i * 1.9) * th;
      leg(b, i, 0.35 * w, 0.4 * Math.sin(TAU * 3 * t + i * 2.7) * th, 0);
    }
    const sh = envelope(t, 0.12, 0.2, 0.5, 0.65) * shiver(t, 9);
    b.body.rot(0.02 * sh, 0, 0.02 * sh);
    b.shellL.rot(0.03 * sh);
    b.shellR.rot(0.03 * sh);
    b.abdomen.scale(1 - 0.06 * span(t, 1.3, 2.4));
  }, (t) => thud(t, [[1.18, 0.07]]));

/**
 * The shell splits: the blow slams it flat, the wing-cases leap on their hinges and spring back crooked, and it
 * rears up with its mandibles wide, shuddering from the antennae to the tail before it drops back onto its legs.
 */
const crack = (): Clip<B> =>
  clip<B>('crack', 0.8, false, (b, t) => {
    timeline(b, t, [
      [0, ST],
      [0.05, { ...legsPose(() => [0, 0, 0.6]), root: [0, 0, 0, 0, -0.07, 0], body: [0.08, 0, 0], head: [0.15, 0, 0], abdomen: [0, 0, 0, 0, 0, 0, 0.92] }, outExpo],
      [0.3, { ...legsPose((i) => (row(i) === 0 ? [0.55, 0.35, 0] : row(i) === 2 ? [0, -0.2, 0.3] : [0.1, 0, 0.2])), root: [0, 0, 0, 0, 0.04, -0.05], body: [-0.42, 0, 0], head: [-0.4, 0, 0], abdomen: [0.1, 0, 0, 0, 0, 0, 1.08], mandR: [0, 0.7, 0], mandL: [0, -0.7, 0], antR: [0.6, 0.5, 0], antL: [0.6, -0.5, 0] }, outCubic],
      [0.55, { ...legsPose((i) => (row(i) === 0 ? [0.4, 0.3, 0.1] : [0, -0.1, 0.2])), root: [0, 0, 0, 0, 0.03, -0.04], body: [-0.32, 0, 0], head: [-0.3, 0, 0], abdomen: [0.06, 0, 0, 0, 0, 0, 1.05], mandR: [0, 0.6, 0], mandL: [0, -0.6, 0], antR: [0.5, 0.45, 0], antL: [0.5, -0.45, 0] }],
      [0.8, ST, inOut],
    ], { head: 0.03, antL: 0.05, antR: 0.05 });
    // The plates jump on their hinges at the blow and rattle as they settle.
    const j = kick(t, 0.02, 5, 6) * span(t, 0, 0.03, outQuad);
    const up = envelope(t, 0, 0.06, 0.12, 0.45, outQuad);
    b.shellL.rot(0.32 * up + 0.08 * j, 0.04 * j, -0.28 * up - 0.06 * j);
    b.shellR.rot(0.28 * up - 0.07 * j, -0.05 * j, 0.32 * up + 0.07 * j);
    // A shudder through everything while it rears.
    const sh = envelope(t, 0.12, 0.22, 0.55, 0.72) * shiver(t, 11);
    b.body.rot(0.025 * sh, 0.02 * sh, 0.03 * sh);
    b.head.rot(0.04 * sh, 0.04 * sh);
    b.shellL.rot(0.04 * sh, 0, -0.03 * sh);
    b.shellR.rot(-0.04 * sh, 0, 0.03 * sh);
    jaws(b, 0.06 * sh);
    b.antL.rot(0.1 * sh);
    b.antR.rot(-0.1 * sh);
  }, (t) => thud(t, [[0.05, 0.08], [0.72, 0.03]]), 'Se le rompe el caparazón');

export const beetleClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), crack()] as Clip[];

/** Clips by library kind (see ../rigs.ts): the cracked beetle shares the skeleton. */
export const CLIPS: Record<string, () => Clip[]> = { beetle: beetleClips, beetleCracked: beetleClips };
